import React from 'react';
import { Route, RouteComponentProps, Switch, useHistory, withRouter } from 'react-router-dom';
import './App.css';
import { LanguageCodeOption } from './speech';
import AuthWrapper, { AuthContext } from './LocalAuthWrapper';
import TranscriptsListWrapper from './TranscriptsListWrapper';
import DraftEditor from './DraftEditor';
import Editor, { AnonymousEditor } from './Editor';
import ErrorMessage from './components/ErrorMessage';
import { Redirect } from 'react-router';
import { isFeatureEnabled } from './features';

import Dashboard from './Dashboard';
import RealTimeCaptioning from './components/RealTimeCaptioning';
import { KEYCLOAK_ADMIN_GROUP } from './config';
import BroadcastDetails from './components/BroadcastDetails';
import AdminBoard from './components/admin/AdminBoard';
import { languageCodeToFriendly } from './helpers';

const languageCodes: LanguageCodeOption[] = [
  {
    key: 'is-IS-x-exp',
    value: 'is-IS-x-exp',
  },
  {
    key: 'is-IS',
    value: 'is-IS',
  },
  {
    key: 'en-US',
    value: 'en-US',
  },
  {
    key: 'is-IS-x-folk',
    value: 'is-IS-x-folk',
  },
].map(({ key, value }) => ({
  key,
  value: value as LanguageCodeOption['value'],
  text: languageCodeToFriendly(value),
}));

type AppErrorBoundaryProps = {
  children: React.ReactNode;
};
type AppErrorBoundaryState = {
  hasError: boolean;
};

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(/* error */ _: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any) {
    console.error('Caught ', error);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorMessage message='Eitthvað fór úrskeiðis... Endurglæddu síðuna og reyndu aftur.' />;
    }

    return this.props.children;
  }
}

const InnerApp = () => (
  <AuthWrapper>
    <Switch>
      <Route
        path='/transcripts/:name/version/:version'
        render={({ match }) => (
          <Editor
            editable={true}
            name={`transcripts/${match.params.name}`}
            version={match.params.version}
            languageCodes={languageCodes}
          />
        )}
      />

      <Route
        path='/transcripts/:name'
        render={({ match, location }) => {
          const t = new URLSearchParams(location.search).get('t');
          const initialSeekTime = t ? Number.parseFloat(t) : null;
          return (
            <Editor
              editable={true}
              languageCodes={languageCodes}
              name={`transcripts/${match.params.name}`}
              initialSeekTime={initialSeekTime ? initialSeekTime : undefined}
            />
          );
        }}
      />

      <Route path='/list-transcripts' render={() => <TranscriptsListWrapper />} />

      <Route path='/dictate' render={() => <DraftEditor />} />

      <Route path='/editor' render={() => <Editor languageCodes={languageCodes} editable={true} />} />

      {isFeatureEnabled('REAL_TIME_CAPTIONING') && (
        <Route
          path='/rauntimatextun/broadcasts/:id'
          render={({ match }) => (
            <AuthContext.Consumer>
              {(auth) => {
                return <BroadcastDetails userId={auth.userDetails?.uid} broadcastId={match.params.id} />;
              }}
            </AuthContext.Consumer>
          )}
        />
      )}

      {isFeatureEnabled('REAL_TIME_CAPTIONING') && (
        <Route path='/rauntimatextun/broadcasts' render={() => <RealTimeCaptioning activeTab='útsendingar' />} />
      )}

      {isFeatureEnabled('REAL_TIME_CAPTIONING') && (
        <Route path='/rauntimatextun' render={() => <Redirect to='/rauntimatextun/broadcasts' />} />
      )}

      <Route path='/' exact strict render={() => <Dashboard languageCodes={languageCodes} />} />

      <Route
        path='/collections/:name'
        exact
        render={({ match }) => (
          <Dashboard languageCodes={languageCodes} collectionName={`collections/${match.params.name}`} />
        )}
      />

      <Route path='/admin' exact render={() => <Redirect to='/admin/users' />} />

      <Route
        path='/admin/:page'
        render={() => (
          <AuthContext.Consumer>
            {(auth) => {
              if (
                auth.userDetails &&
                auth.userDetails.groups &&
                auth.userDetails.groups.includes(KEYCLOAK_ADMIN_GROUP)
              ) {
                return <AdminBoard />;
              }
              return <Redirect to='/' />;
            }}
          </AuthContext.Consumer>
        )}
      />

      <Route render={() => 'Not found'} />
    </Switch>
  </AuthWrapper>
);

type AppProps = RouteComponentProps;

function App(_: AppProps) {
  const history = useHistory();

  if (window.location.hash.startsWith('#/')) {
    history.replace(window.location.hash.replace('#', ''));
  }

  return (
    <Switch>
      <Route
        path='/public-transcripts/:name'
        render={({ match }) => (
          <AnonymousEditor editable={false} name={`transcripts/${match.params.name}`} languageCodes={languageCodes} />
        )}
      />

      <Route
        render={() => (
          <AppErrorBoundary>
            <InnerApp />
          </AppErrorBoundary>
        )}
      />
    </Switch>
  );
}

export default withRouter(App);
